import React, { useEffect, useState, useCallback } from "react";
import { authenticatedGet, authenticatedPost } from "../utils/apiUtils";
import Swal from "sweetalert2";
import { useTransferNotifications } from "../contexts/TransferNotificationsContext";

const ViewPendingTransfers = () => {
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [loading, setLoading] = useState(false); // Initialize to false to allow initial fetch
  const [showActionableOnly, setShowActionableOnly] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [currentSecurityLevel, setCurrentSecurityLevel] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetDetailsLoading, setAssetDetailsLoading] = useState(false);
  const [showAssetDetailsModal, setShowAssetDetailsModal] = useState(false);
  const {
    refreshPendingTransfers,
    pendingCount,
    previousCount,
    updatePendingCount,
  } = useTransferNotifications();

  // Fetch transfers on component mount and when pendingCount changes
  useEffect(() => {
    // Refresh if:
    // 1. pendingCount is different from previousCount (including 0 to 1 transitions)
    // 2. We have any edge case with undefined vs defined values
    if (
      pendingCount !== previousCount ||
      (previousCount === undefined && pendingCount !== undefined) ||
      (pendingCount !== undefined && previousCount === undefined)
    ) {
      console.log(
        `📊 Badge count changed from ${
          previousCount !== undefined ? previousCount : "undefined"
        } to ${pendingCount} - refreshing transfers table`
      );
      fetchPendingTransfers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCount, previousCount]); // Do NOT include fetchPendingTransfers to avoid circular dependency

  // Initial fetch on component mount
  useEffect(() => {
    console.log("🔄 Initial fetch of pending transfers");
    setLoading(true); // Set loading to true before the initial fetch
    fetchPendingTransfers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array for component mount only

  // Function to fetch pending transfers using authenticated API utility
  const fetchPendingTransfers = useCallback(async () => {
    // Only prevent fetching if we're already loading AND have existing data
    // This allows refreshes when transitioning from empty to populated
    if (loading && pendingTransfers.length > 0) {
      console.log(
        "⏳ Already fetching transfers with existing data, skipping duplicate fetch"
      );
      return;
    }

    setLoading(true);

    try {
      console.log("📡 Fetching pending transfers...");
      const response = await authenticatedGet("/transfers/pending");

      if (response.ok) {
        const data = await response.json();
        // Filter out completed, reclaimed, and other non-pending statuses
        const filteredData = data.filter(
          (t) => t.status !== "Completed" && t.status !== "Reclaimed"
        );
        setPendingTransfers(filteredData);

        // Count the actionable transfers to ensure our badge is up-to-date
        const actionableCount = filteredData.filter(
          (t) =>
            (t.isRequester && t.status === "PendingRequesterReview") ||
            (t.isOwner && t.status === "PendingOwnerApproval") ||
            (t.isOwner && t.status === "DeniedByRequester")
        ).length;

        // Update the count in the context (this will refresh other components using the badge)
        console.log(
          `📊 Setting badge count to ${actionableCount} actionable items`
        );
        updatePendingCount(actionableCount);

        console.log("✅ Pending transfers loaded successfully");
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error("❌ Error fetching pending transfers:", error);
      Swal.fire({
        icon: "error",
        title: "Loading Failed",
        text: "Error fetching pending transfers. Please try again.",
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, updatePendingCount, pendingTransfers.length]); // Use updatePendingCount instead of refreshPendingTransfers

  const completeTransfer = async (transferId) => {
    // First check if authentication is needed
    const authShown = await checkAuthenticationBeforeAction(
      completeTransfer,
      transferId
    );
    if (authShown) return; // Exit if authentication modal was shown

    try {
      console.log(`📡 Completing transfer ${transferId}...`);

      const response = await authenticatedPost(
        `/transfers/requester-decision/${transferId}`,
        { approved: true },
        { handleErrors: false } // Let us handle errors manually
      );

      if (response.ok || response.status === 302) {
        console.log(
          `✅ Transfer completed successfully (Status: ${response.status})`
        );

        // Even with a 302, we consider this successful since our API handles redirects
        Swal.fire({
          icon: "success",
          title: "Transfer Completed!",
          text: "The transfer was successfully completed.",
        });

        // Trigger refresh of the notification badge
        refreshPendingTransfers();

        // Also refresh the full list to get the updated state from the server
        setTimeout(() => {
          fetchPendingTransfers();
        }, 500);
      } else {
        // For status codes like 401 (Unauthorized), check for authentication requirements
        const responseData = await response.json().catch(() => ({}));

        if (responseData.requiresAuthentication) {
          console.log("Authentication required, showing password modal");
          setCurrentSecurityLevel(responseData.securityLevel);
          setPendingAction(() => () => completeTransfer(transferId));
          setShowPasswordModal(true);
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error("❌ Error completing transfer:", error);

      // Check if this is an authentication error
      let errorData;
      try {
        // The response object might be available as a property on the error
        // if it was thrown from the fetch call
        if (error.response) {
          errorData = await error.response.json();
        } else if (error.message && error.message.includes("fetch")) {
          // This might be a network error
          errorData = {
            message: "Network error. Please check your connection.",
          };
        } else {
          // For other types of errors, attempt to check the original response
          errorData = {};
        }
      } catch {
        // If we can't parse it, just use an empty object
        errorData = {};
      }

      // Check if authentication is required
      const requiresAuth =
        errorData?.requiresAuthentication === true ||
        (errorData?.message &&
          errorData.message.includes("authentication required")) ||
        error.status === 401 ||
        error.status === 403;

      if (requiresAuth) {
        console.log("Authentication required, showing password modal");
        setCurrentSecurityLevel(errorData?.securityLevel || "Maximum");
        setPendingAction(() => () => completeTransfer(transferId));
        setShowPasswordModal(true);
      } else {
        Swal.fire({
          icon: "error",
          title: "Transfer Failed",
          text:
            errorData?.message ||
            "There was a problem completing the transfer. Please try again.",
        });
      }
    }
  };

  const rejectTransfer = async (transferId) => {
    try {
      console.log(`📡 Rejecting transfer ${transferId}...`);

      const response = await authenticatedPost(
        `/transfers/requester-decision/${transferId}`,
        { approved: false },
        { handleErrors: false } // Let us handle errors manually
      );

      if (response.ok || response.status === 302) {
        console.log(
          `✅ Transfer rejected successfully (Status: ${response.status})`
        );

        // Even with a 302, we consider this successful since our API handles redirects
        Swal.fire({
          icon: "success",
          title: "Transfer Rejected",
          text: "The transfer was successfully rejected.",
        });

        // Trigger refresh of the notification badge
        refreshPendingTransfers();

        // Also refresh the full list to get the updated state from the server
        setTimeout(() => {
          fetchPendingTransfers();
        }, 500);
      } else {
        // Don't check for authentication, just throw the error
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error("❌ Error rejecting transfer:", error);

      // Show a simple error message without authentication checks
      Swal.fire({
        icon: "error",
        title: "Transfer Failed",
        text: "There was a problem rejecting the transfer. Please try again.",
      });
    }
  };

  // Helper function to check authentication status before performing high-security actions
  const checkAuthenticationBeforeAction = async (action, transferId) => {
    try {
      // Check current authentication status first
      console.log("📡 Checking authentication status before action...");

      // Get the user before making the request to check auth status
      try {
        const user = await window.userManager?.getUser();
        console.log("Current user state:", user ? "User available" : "No user");
        if (user) {
          console.log(
            "Token expires at:",
            new Date(user.expires_at * 1000).toLocaleString()
          );
          console.log("Token expired:", user.expired);
          console.log("Has access token:", !!user.access_token);
        }
      } catch (userError) {
        console.error("Error getting user:", userError);
      }

      const statusResponse = await authenticatedGet(
        "/encryption/session-status",
        { handleErrors: false }
      );

      // Log full response details
      console.log("Status response:", {
        ok: statusResponse.ok,
        status: statusResponse.status,
        statusText: statusResponse.statusText,
        redirected: statusResponse.redirected,
        type: statusResponse.type,
        contentType: statusResponse.headers.get("content-type"),
        allHeaders: [...statusResponse.headers.entries()],
      });

      // Check if we got HTML instead of JSON (this happens when session expires)
      const contentType = statusResponse.headers.get("content-type");
      if (contentType && contentType.includes("text/html")) {
        console.log(
          "Received HTML response instead of JSON - likely session expired"
        );

        // Let's try to get the first few characters of the response to confirm it's HTML
        try {
          const clonedResponse = statusResponse.clone();
          const text = await clonedResponse.text();
          console.log("Response begins with:", text.substring(0, 100));

          // If it contains <!doctype html> or <html, it's definitely HTML
          if (
            text.toLowerCase().includes("<!doctype html>") ||
            text.toLowerCase().includes("<html")
          ) {
            console.log("Confirmed HTML response - session likely expired");

            // Try to refresh the token explicitly
            try {
              console.log("Attempting to refresh auth token...");
              await window.userManager?.signinSilent();
              console.log("Token refreshed successfully");

              // Instead of showing the auth modal immediately, try the action again
              // after a short delay to allow the token refresh to propagate
              setTimeout(() => action(transferId), 500);
              return true;
            } catch (refreshError) {
              console.error("Token refresh failed:", refreshError);
              // Fall back to asking for the encryption password
              setCurrentSecurityLevel("Maximum"); // Default to Maximum security level
              setPendingAction(() => () => action(transferId));
              setShowPasswordModal(true);
              return true; // Authentication modal shown
            }
          }
        } catch (textError) {
          console.error("Error reading response text:", textError);
        }
      }

      if (statusResponse.ok) {
        const statusData = await statusResponse.json().catch(() => ({}));
        console.log("Authentication status:", statusData);

        // If the status indicates we need authentication for Enhanced/Maximum security
        if (
          (statusData.securityLevel === "Enhanced" ||
            statusData.securityLevel === "Maximum") &&
          (statusData.requiresAuthentication === true ||
            statusData.authenticated === false ||
            statusData.hasActiveSession === false)
        ) {
          console.log(
            `Authentication required for ${statusData.securityLevel} security level`
          );
          setCurrentSecurityLevel(statusData.securityLevel);
          setPendingAction(() => () => action(transferId));
          setShowPasswordModal(true);
          return true; // Authentication modal shown
        }
      }

      // If no authentication needed or check failed, proceed with the action
      return false; // No authentication needed
    } catch (error) {
      console.error("Error checking authentication status:", error);
      return false; // Continue with normal flow on error
    }
  };

  const approveOwner = async (transferId) => {
    // First check if authentication is needed
    const authShown = await checkAuthenticationBeforeAction(
      approveOwner,
      transferId
    );
    if (authShown) return; // Exit if authentication modal was shown

    try {
      console.log(`📡 Approving transfer as owner ${transferId}...`);
      console.log(
        "Current session state:",
        document.cookie ? "Has cookies" : "No cookies"
      );

      // Check OIDC status directly before making the API call
      try {
        const user = await window.userManager?.getUser();
        if (!user || user.expired) {
          console.log("OIDC session expired, showing authentication modal");
          setCurrentSecurityLevel("Maximum");
          setPendingAction(() => () => approveOwner(transferId));
          setShowPasswordModal(true);
          return;
        } else {
          console.log(
            "OIDC session active - token expires:",
            new Date(user.expires_at * 1000).toLocaleString()
          );
        }
      } catch (oidcError) {
        console.error("Error checking OIDC status:", oidcError);
      }

      // Store the raw response for backup error handling
      let rawResponse = null;

      try {
        const response = await authenticatedPost(
          `/transfers/approve-owner/${transferId}`,
          {},
          { handleErrors: false } // Let us handle errors manually
        );

        // Save the raw response as a backup
        rawResponse = response.clone();

        // Immediately log the full response for debugging
        console.log(`Response status: ${response.status}`, response);
        console.log("Response headers:", [...response.headers.entries()]);

        // For 400 responses, try to get more details
        if (response.status === 400) {
          try {
            const errorDetails = await response.text();
            console.log("Error details for 400:", errorDetails);

            // Try to determine if this is an authentication issue in disguise
            if (
              errorDetails.includes("authentication") ||
              errorDetails.includes("unauthorized") ||
              errorDetails.includes("auth") ||
              errorDetails.includes("session")
            ) {
              console.log(
                "400 error might be auth-related, showing password modal"
              );
              setCurrentSecurityLevel("Maximum"); // Default to Maximum security level
              setPendingAction(() => () => approveOwner(transferId));
              setShowPasswordModal(true);
              return;
            }
          } catch (textErr) {
            console.error(
              "Failed to get error details from 400 response:",
              textErr
            );
          }
        }

        if (response.ok || response.status === 302) {
          console.log(
            `✅ Transfer approved successfully (Status: ${response.status})`
          );

          // Even with a 302, we consider this successful since our API handles redirects
          Swal.fire({
            icon: "success",
            title: "Transfer Approved",
            text: "You've approved the transfer. The requester can now review the item details.",
          });

          // Trigger refresh of the notification badge
          refreshPendingTransfers();

          // Also refresh the full list to get the updated state from the server
          setTimeout(() => {
            fetchPendingTransfers();
          }, 500);
        } else {
          // For status codes like 401 (Unauthorized), check for authentication requirements
          try {
            const responseData = await response.json();
            console.log("Response data:", responseData);

            // Check explicitly for authentication requirements
            if (
              responseData &&
              (responseData.requiresAuthentication === true ||
                (responseData.message &&
                  responseData.message
                    .toLowerCase()
                    .includes("authentication required")) ||
                (responseData.message &&
                  responseData.message
                    .toLowerCase()
                    .includes("session authentication")))
            ) {
              console.log(
                "Authentication required from response data, showing password modal"
              );
              setCurrentSecurityLevel(responseData.securityLevel || "Maximum");
              setPendingAction(() => () => approveOwner(transferId));
              setShowPasswordModal(true);
              return; // Exit early
            }
          } catch (jsonError) {
            console.error("Failed to parse response JSON:", jsonError);

            // Fall through to checking status codes
          }

          // Check status code as a fallback
          if (response.status === 401 || response.status === 403) {
            console.log("Status code indicates authentication required");
            setCurrentSecurityLevel("Maximum"); // Default to Maximum security level
            setPendingAction(() => () => approveOwner(transferId));
            setShowPasswordModal(true);
            return; // Exit early
          }

          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (fetchError) {
        // If the fetch itself failed, we need to check for authentication in a different way
        console.error("Fetch operation failed:", fetchError);

        // Try to read the raw response if available
        if (rawResponse) {
          try {
            const errorText = await rawResponse.text();
            console.log("Raw response text:", errorText);

            // Check if the error text contains authentication requirement indicators
            if (
              errorText.includes("requiresAuthentication") ||
              errorText.includes("authentication required") ||
              errorText.includes("Session authentication")
            ) {
              console.log("Authentication requirement found in raw response");
              setCurrentSecurityLevel("Maximum"); // Default to Maximum security level
              setPendingAction(() => () => approveOwner(transferId));
              setShowPasswordModal(true);
              return; // Exit early
            }
          } catch (textError) {
            console.error("Failed to read raw response text:", textError);
            // Continue to the outer catch block
          }
        }

        // Re-throw for the outer catch block to handle
        throw fetchError;
      }
    } catch (error) {
      console.error("❌ Error approving transfer:", error);

      // Direct check for authentication indicators in the error message
      if (
        error.message &&
        (error.message.includes("authentication required") ||
          error.message.includes("Session authentication") ||
          error.message.includes("requiresAuthentication") ||
          error.message.includes("Unauthorized") ||
          error.message.includes("Forbidden"))
      ) {
        console.log("Authentication requirement found in error message");
        setCurrentSecurityLevel("Maximum"); // Default to Maximum security level
        setPendingAction(() => () => approveOwner(transferId));
        setShowPasswordModal(true);
        return; // Exit early
      }

      // Make a direct request to check the authentication status
      try {
        const authCheckResponse = await authenticatedGet(
          "/encryption/session-status",
          { handleErrors: false }
        );
        const authStatus = await authCheckResponse.json().catch(() => ({}));

        if (
          authStatus.authenticated === false ||
          authStatus.requiresAuthentication === true
        ) {
          console.log("Authentication check indicates authentication required");
          setCurrentSecurityLevel(authStatus.securityLevel || "Maximum");
          setPendingAction(() => () => approveOwner(transferId));
          setShowPasswordModal(true);
          return; // Exit early
        }
      } catch (authCheckError) {
        console.error("Authentication check failed:", authCheckError);
        // Continue with normal error handling
      }

      // If we got here, it's not an authentication issue, show detailed error
      console.error("Error details:", error);

      // Try to extract more helpful error info
      let errorMessage =
        "There was a problem approving the transfer. Please try again.";

      if (error.message) {
        // Clean up common error messages
        if (error.message.includes("API POST request failed: 400")) {
          errorMessage =
            "The server couldn't process this approval request. This might be due to the transfer being in an invalid state or already processed.";
        } else if (error.message.includes("fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else {
          errorMessage = error.message;
        }
      }

      Swal.fire({
        icon: "error",
        title: "Approval Failed",
        text: errorMessage,
      });
    }
  };

  const reclaimTransfer = async (transferId) => {
    // First check if authentication is needed
    const authShown = await checkAuthenticationBeforeAction(
      reclaimTransfer,
      transferId
    );
    if (authShown) return; // Exit if authentication modal was shown

    try {
      console.log(`📡 Reclaiming transfer ${transferId}...`);

      const response = await authenticatedPost(
        `/transfers/reclaim/${transferId}`,
        {},
        { handleErrors: false } // Let us handle errors manually
      );

      if (response.ok || response.status === 302) {
        console.log(
          `✅ Transfer reclaimed successfully (Status: ${response.status})`
        );

        // Even with a 302, we consider this successful since our API handles redirects
        Swal.fire({
          icon: "success",
          title: "Item Reclaimed",
          text: "You have successfully reclaimed your item.",
        });

        // Trigger refresh of the notification badge
        refreshPendingTransfers();

        // Also refresh the full list to get the updated state from the server
        setTimeout(() => {
          fetchPendingTransfers();
        }, 500);
      } else {
        // For status codes like 401 (Unauthorized), check for authentication requirements
        const responseData = await response.json().catch(() => ({}));

        if (responseData.requiresAuthentication) {
          console.log("Authentication required, showing password modal");
          setCurrentSecurityLevel(responseData.securityLevel);
          setPendingAction(() => () => reclaimTransfer(transferId));
          setShowPasswordModal(true);
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error("❌ Error reclaiming transfer:", error);

      // Check if this is an authentication error
      let errorData;
      try {
        // The response object might be available as a property on the error
        // if it was thrown from the fetch call
        if (error.response) {
          errorData = await error.response.json();
        } else if (error.message && error.message.includes("fetch")) {
          // This might be a network error
          errorData = {
            message: "Network error. Please check your connection.",
          };
        } else {
          // For other types of errors, attempt to check the original response
          errorData = {};
        }
      } catch {
        // If we can't parse it, just use an empty object
        errorData = {};
      }

      // Check if authentication is required
      const requiresAuth =
        errorData?.requiresAuthentication === true ||
        (errorData?.message &&
          errorData.message.includes("authentication required")) ||
        error.status === 401 ||
        error.status === 403;

      if (requiresAuth) {
        console.log("Authentication required, showing password modal");
        setCurrentSecurityLevel(errorData?.securityLevel || "Maximum");
        setPendingAction(() => () => reclaimTransfer(transferId));
        setShowPasswordModal(true);
      } else {
        Swal.fire({
          icon: "error",
          title: "Reclaim Failed",
          text:
            errorData?.message ||
            "There was a problem reclaiming the item. Please try again.",
        });
      }
    }
  };

  const denyOwner = async (transferId) => {
    try {
      console.log(`📡 Denying transfer ${transferId} as owner...`);

      const response = await authenticatedPost(
        `/transfers/deny-owner/${transferId}`,
        {},
        { handleErrors: false } // Let us handle errors manually
      );

      if (response.ok || response.status === 302) {
        console.log(
          `✅ Transfer denied successfully (Status: ${response.status})`
        );

        // Even with a 302, we consider this successful since our API handles redirects
        Swal.fire({
          icon: "success",
          title: "Transfer Denied",
          text: "You've denied the transfer request.",
        });

        // Trigger refresh of the notification badge
        refreshPendingTransfers();

        // Also refresh the full list to get the updated state from the server
        setTimeout(() => {
          fetchPendingTransfers();
        }, 500);
      } else {
        // Don't check for authentication, just throw the error
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error("❌ Error denying transfer:", error);

      // Show a simple error message without authentication checks
      Swal.fire({
        icon: "error",
        title: "Denial Failed",
        text: "There was a problem denying the transfer. Please try again.",
      });
    }
  };

  // Handle password submission for authentication
  const handlePasswordSubmit = async () => {
    if (!password.trim()) {
      Swal.fire({
        icon: "error",
        title: "Password Required",
        text: "Please enter your encryption password to continue.",
      });
      return;
    }

    try {
      console.log("📡 Authenticating session...");
      const response = await authenticatedPost(
        "/encryption/authenticate-session",
        { password: password },
        { handleErrors: false }
      );

      console.log("Authentication response:", response);

      if (response.ok) {
        // Parse the response to get any additional information
        const responseData = await response.json().catch(() => ({}));
        console.log("Authentication successful, response data:", responseData);

        // Clear the password field
        setPassword("");
        setShowPasswordModal(false);

        // Execute the pending action if there is one
        if (pendingAction) {
          console.log("🔄 Executing pending action after authentication");
          // Add a small delay to ensure the authentication is fully processed on the server
          setTimeout(() => {
            pendingAction();
            setPendingAction(null);
          }, 500);
        }

        // Show a success message
        Swal.fire({
          icon: "success",
          title: "Authentication Successful",
          text: "You are now authenticated for the current session.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        // Try to get more information about the authentication failure
        const responseData = await response.json().catch(() => ({}));
        console.error("❌ Authentication failed", responseData);

        // Don't show alert - just leave the modal open for the user to try again
        // The user can see that authentication failed because the modal stays open
      }
    } catch (error) {
      console.error("❌ Error during authentication:", error);
      Swal.fire({
        icon: "error",
        title: "Authentication Error",
        text: "There was a problem with authentication. Please try again.",
      });
    }
  };

  // Function to fetch asset details
  const fetchAssetDetails = async (assetId, transferId) => {
    if (!assetId || !transferId) return;

    setAssetDetailsLoading(true);
    try {
      console.log(
        `📡 Fetching details for item ${assetId} from transfer ${transferId}...`
      );
      // Use the transfer API endpoint to get asset details during transfer
      const response = await authenticatedGet(`/transfers/asset/${transferId}`);

      if (response.ok) {
        const data = await response.json();
        console.log("✅ Item details loaded successfully", data);
        setSelectedAsset(data);
        setShowAssetDetailsModal(true);
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error("❌ Error fetching item details:", error);
      Swal.fire({
        icon: "error",
        title: "Loading Failed",
        text: "Error fetching item details. Please try again.",
      });
    } finally {
      setAssetDetailsLoading(false);
    }
  };

  // Format transfer status for better readability
  const formatStatus = (status) => {
    if (!status) return "Unknown Status";

    // Ensure status is a string
    const statusStr = String(status);

    switch (statusStr) {
      case "PendingOwnerApproval":
        return "Pending Owner Approval";
      case "PendingRequesterReview":
        return "Pending Requester Review";
      case "DeniedByRequester":
        return "Denied by Requester";
      case "DeniedByOwner":
        return "Denied by Owner";
      case "Completed":
        return "Completed";
      case "Reclaimed":
        return "Reclaimed by Owner";
      default:
        // Add spaces before capital letters and capitalize first letter
        return statusStr
          .replace(/([A-Z])/g, " $1")
          .trim()
          .replace(/^\w/, (c) => c.toUpperCase());
    }
  };

  // Check if transfer requires action by the current user
  const requiresAction = (transfer) => {
    return (
      (transfer.isRequester && transfer.status === "PendingRequesterReview") ||
      (transfer.isOwner && transfer.status === "PendingOwnerApproval") ||
      (transfer.isOwner && transfer.status === "DeniedByRequester")
    );
  };

  if (loading && pendingTransfers.length === 0) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading transfers...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Pending Item Transfers</h2>
        <div className="d-flex align-items-center">
          {loading && (
            <span className="badge badge-pill badge-secondary me-2">
              <small>Refreshing...</small>
            </span>
          )}
          {pendingTransfers.filter(requiresAction).length > 0 && (
            <span className="badge badge-pill badge-info me-2">
              {pendingTransfers.filter(requiresAction).length}{" "}
              {pendingTransfers.filter(requiresAction).length === 1
                ? "item"
                : "items"}{" "}
              need
              {pendingTransfers.filter(requiresAction).length === 1
                ? "s"
                : ""}{" "}
              your action
            </span>
          )}
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => {
              refreshPendingTransfers();
              fetchPendingTransfers();
            }}
            disabled={loading}
          >
            <i className="fas fa-sync-alt me-1"></i>
            Refresh
          </button>
        </div>
      </div>

      <div className="form-check mb-3">
        <input
          type="checkbox"
          className="form-check-input"
          id="showActionableOnly"
          checked={showActionableOnly}
          onChange={(e) => setShowActionableOnly(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="showActionableOnly">
          Show only transfers requiring my action
        </label>
      </div>

      {pendingTransfers.length === 0 ? (
        <p>No pending transfers available.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Item ID</th>
              <th>Item Name</th>
              <th>Status</th>
              <th>User</th>
              <th>Request Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {pendingTransfers
              .filter(
                (transfer) => !showActionableOnly || requiresAction(transfer)
              )
              .map((transfer) => (
                <tr
                  key={transfer.id}
                  className={requiresAction(transfer) ? "table-active" : ""}
                >
                  <td>{transfer.assetId}</td>
                  <td>
                    {transfer.isRequester &&
                    transfer.status === "PendingRequesterReview" ? (
                      <span
                        className="item-name-link"
                        onClick={() =>
                          fetchAssetDetails(transfer.assetId, transfer.id)
                        }
                        style={{
                          cursor: "pointer",
                          color: "#007bff",
                          textDecoration: "underline",
                        }}
                        title="Click to view item details"
                      >
                        {transfer.assetName}
                      </span>
                    ) : (
                      transfer.assetName
                    )}
                  </td>
                  <td>
                    <span
                      className={
                        transfer.status === "Completed"
                          ? "text-success"
                          : transfer.status === "DeniedByRequester"
                          ? "text-danger"
                          : transfer.status === "DeniedByOwner"
                          ? "text-danger"
                          : transfer.status === "Reclaimed"
                          ? "text-warning"
                          : requiresAction(transfer)
                          ? "text-info font-weight-bold"
                          : "text-primary"
                      }
                    >
                      {/* Always format the status regardless of user role */}
                      {formatStatus(transfer.status)}
                      {requiresAction(transfer) && (
                        <span className="badge badge-pill badge-info ml-2">
                          Action Required
                        </span>
                      )}
                    </span>
                  </td>
                  <td>
                    {transfer.isOwner ? (
                      <span>Requester: {transfer.requesterUsername}</span>
                    ) : (
                      <span>Owner: {transfer.ownerUsername}</span>
                    )}
                  </td>
                  <td>
                    {(() => {
                      // Parse the date from the server
                      const dateStr = transfer.requestDate;

                      // Since the server sends dates without timezone info (e.g. 2025-08-02T19:38:26.195895)
                      // and the actual time is stored in UTC, we need to handle this explicitly

                      // If the date doesn't end with Z, append it to indicate it's UTC
                      const dateWithTimezone =
                        typeof dateStr === "string" && !dateStr.endsWith("Z")
                          ? dateStr + "Z"
                          : dateStr;

                      // Now parse as a proper UTC date
                      const date = new Date(dateWithTimezone);

                      // Format the date in the user's local timezone
                      return date.toLocaleString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      });
                    })()}
                    <span className="text-muted d-block small">
                      (Your timezone:{" "}
                      {Intl.DateTimeFormat().resolvedOptions().timeZone})
                    </span>
                  </td>
                  <td>
                    {transfer.isRequester &&
                      transfer.status === "PendingRequesterReview" && (
                        <>
                          <button
                            className="btn btn-success"
                            onClick={() => completeTransfer(transfer.id)}
                            style={{ marginRight: "10px" }}
                          >
                            Accept Transfer
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => rejectTransfer(transfer.id)}
                          >
                            Reject Transfer
                          </button>
                        </>
                      )}
                    {transfer.isOwner &&
                      transfer.status === "PendingOwnerApproval" && (
                        <>
                          <button
                            className="btn btn-primary"
                            onClick={() => approveOwner(transfer.id)}
                            style={{ marginRight: "10px" }}
                          >
                            Approve Transfer
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => denyOwner(transfer.id)}
                          >
                            Deny Transfer
                          </button>
                        </>
                      )}
                    {transfer.isOwner &&
                      transfer.status === "DeniedByRequester" && (
                        <button
                          className="btn btn-warning"
                          onClick={() => reclaimTransfer(transfer.id)}
                        >
                          Reclaim Item
                        </button>
                      )}
                    {/* Status indicators are now shown in the status column with proper formatting */}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}

      {/* Password Authentication Modal */}
      {showPasswordModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-lock me-2"></i>
                  Authentication Required
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowPasswordModal(false)}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  Your {currentSecurityLevel} security level requires
                  authentication to complete this action. Please enter your
                  encryption password to continue.
                </p>
                <div className="form-group">
                  <label htmlFor="modalPassword" className="form-label">
                    Encryption Password
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="modalPassword"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your encryption password"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handlePasswordSubmit();
                      }
                    }}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPasswordModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handlePasswordSubmit}
                >
                  Authenticate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showPasswordModal && <div className="modal-backdrop fade show"></div>}

      {/* Asset Details Modal */}
      {showAssetDetailsModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="fas fa-exchange-alt me-2"></i>
                  Item in Transit
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  aria-label="Close"
                  onClick={() => setShowAssetDetailsModal(false)}
                ></button>
              </div>
              <div className="modal-body" style={{ padding: "1.5rem" }}>
                {assetDetailsLoading ? (
                  <div className="text-center my-5 py-5">
                    <div
                      className="spinner-border text-primary"
                      role="status"
                      style={{ width: "3rem", height: "3rem" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-primary fw-bold">
                      Loading item details...
                    </p>
                  </div>
                ) : selectedAsset ? (
                  <div>
                    <div className="mb-4">
                      <h6 className="text-primary fw-bold mb-3">
                        Basic Information
                      </h6>
                      <table className="table table-sm table-striped table-bordered">
                        <tbody>
                          <tr>
                            <th
                              scope="row"
                              style={{
                                width: "30%",
                                backgroundColor: "#f8f9fa",
                              }}
                            >
                              Name
                            </th>
                            <td>{selectedAsset.name}</td>
                          </tr>
                          <tr>
                            <th
                              scope="row"
                              style={{ backgroundColor: "#f8f9fa" }}
                            >
                              Type
                            </th>
                            <td>{selectedAsset.assetType}</td>
                          </tr>
                          <tr>
                            <th
                              scope="row"
                              style={{ backgroundColor: "#f8f9fa" }}
                            >
                              Description
                            </th>
                            <td>{selectedAsset.description || "N/A"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Item Details */}
                    {selectedAsset.properties &&
                    selectedAsset.properties.length > 0 ? (
                      <div>
                        <h6 className="text-primary fw-bold mb-3">
                          Additional Details
                        </h6>
                        <table className="table table-sm table-striped table-bordered">
                          <tbody>
                            {selectedAsset.properties.map((property) => (
                              <tr key={property.id}>
                                <th
                                  scope="row"
                                  style={{
                                    width: "30%",
                                    backgroundColor: "#f8f9fa",
                                  }}
                                >
                                  {property.name}
                                </th>
                                <td>{property.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted mt-3">
                        No additional details for this item.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i
                      className="fas fa-exclamation-circle text-secondary"
                      style={{ fontSize: "3rem" }}
                    ></i>
                    <p className="mt-3 text-secondary">
                      No item details available.
                    </p>
                  </div>
                )}
              </div>
              <div className="modal-footer d-flex justify-content-between bg-light">
                <div>
                  {selectedAsset && selectedAsset.isEncrypted && (
                    <span className="badge bg-warning text-dark">
                      <i className="fas fa-lock me-1"></i> Encrypted
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  onClick={() => setShowAssetDetailsModal(false)}
                  style={{ minWidth: "120px" }}
                >
                  <i className="fas fa-times-circle me-2"></i>Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAssetDetailsModal && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
};

export default ViewPendingTransfers;
